// WWW-15236 — helpers for scheduled push notifications.
//
// A scheduled push is a persisted message with `status === 'scheduled'`, a
// `scheduledSendTime` (ISO-8601) and `brazeScheduleIds` (the per-campaign Braze
// schedule ids used to reschedule / cancel). Delivery is Braze-native, so the
// app has no send-time callback: once `scheduledSendTime` has passed we treat the
// push as sent (it drops out of the Scheduled Pushes list and reads as delivered
// in the Activity Log).

export const SCHEDULED_STATUS = 'scheduled';

// Push scheduling is always interpreted in Eastern time (the newsroom's zone),
// independent of the editor's browser timezone. `Intl` supplies the correct
// offset per date, so EDT (−04:00, summer) vs EST (−05:00, winter) is handled
// automatically — the editor picks a wall-clock time and it means that time in
// Philadelphia whether they're on an Eastern laptop or not.
export const EASTERN_TZ = 'America/New_York';

// Offset (ms) of `timeZone` from UTC at a given instant. Positive = ahead of UTC.
// For America/New_York this is −4h in summer, −5h in winter. Uses
// Intl.formatToParts (not `new Date(toLocaleString(...))`, whose locale-string
// parsing is unspecified across engines) to read the zone's wall-clock, then
// diffs that (interpreted as UTC) against the real instant.
function tzOffsetMs(timeZone, instant) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(instant)
    .reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});

  const asUtc = Date.UTC(
    +parts.year,
    +parts.month - 1,
    +parts.day,
    +parts.hour % 24,
    +parts.minute,
    +parts.second,
  );

  return asUtc - instant.getTime();
}

// Interpret the wall-clock fields of `wall` (a Date whose local getFullYear/
// getHours/etc. are the numbers the editor picked in the datepicker) as Eastern
// time, and return the true absolute instant as a UTC ISO-8601 string for Braze.
// e.g. wall = Aug 15 2:30 PM → 2026-08-15T18:30:00.000Z (EDT); Jan 15 2:30 PM →
// 2026-01-15T19:30:00.000Z (EST). Returns null for empty / invalid input.
export function zonedWallTimeToUtcIso(wall) {
  if (!wall) return null;

  const d = wall instanceof Date ? wall : new Date(wall);

  if (Number.isNaN(d.getTime())) return null;

  // Treat the picked wall-clock as if it were UTC, then shift by the Eastern
  // offset to land on the real UTC instant. The offset is refined in two passes:
  // near a DST boundary the provisional (wall-as-UTC) instant can sit on the
  // wrong side of the transition, so we re-read the offset at the candidate
  // instant. Without the second pass, early-morning times on the spring-forward
  // day (e.g. 3:30 AM on the second Sunday of March) pick up the winter −5h
  // offset and are delivered an hour late.
  const provisional = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes());
  const firstOffset = tzOffsetMs(EASTERN_TZ, new Date(provisional));
  const offset = tzOffsetMs(EASTERN_TZ, new Date(provisional - firstOffset));

  return new Date(provisional - offset).toISOString();
}

// Inverse of zonedWallTimeToUtcIso: given a stored UTC ISO, return a Date whose
// LOCAL fields equal the Eastern wall-clock — so react-datepicker shows the same
// time the editor scheduled, regardless of their browser timezone. Used to
// preload the picker when editing an existing scheduled push.
export function utcIsoToZonedWallTime(iso) {
  if (!iso) return null;

  const instant = new Date(iso);

  if (Number.isNaN(instant.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TZ,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
    .formatToParts(instant)
    .reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});

  // hour12:false can emit '24' for midnight in some engines — normalize to 0.
  const hour = parseInt(parts.hour, 10) % 24;

  return new Date(+parts.year, +parts.month - 1, +parts.day, hour, +parts.minute);
}

// "Now" as an Eastern wall-clock Date (local fields = current ET time), for the
// datepicker's min bounds so a past ET time can't be chosen.
export function nowEasternWall() {
  return utcIsoToZonedWallTime(new Date().toISOString());
}

// The Eastern zone abbreviation ('EDT' or 'EST') for a given instant — shown next
// to the picker so editors know the time is Eastern. Defaults to now.
export function easternAbbrev(instant = new Date()) {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: EASTERN_TZ, timeZoneName: 'short' })
    .formatToParts(instant)
    .find((p) => p.type === 'timeZoneName');

  return part ? part.value : 'ET';
}

function scheduledTimeMs(message) {
  if (!message || message.status !== SCHEDULED_STATUS) return NaN;

  return message.scheduledSendTime ? new Date(message.scheduledSendTime).getTime() : NaN;
}

// Still pending — belongs in the Scheduled Pushes list.
export function isFutureScheduled(message, now = Date.now()) {
  const t = scheduledTimeMs(message);

  return !Number.isNaN(t) && t > now;
}

// Its scheduled time has passed — Braze has (best-effort) sent it, so it belongs
// in the Activity Log, not the Scheduled Pushes list.
export function isPastScheduled(message, now = Date.now()) {
  const t = scheduledTimeMs(message);

  return !Number.isNaN(t) && t <= now;
}

// Render a scheduled time in Eastern with the zone abbreviation, so every viewer
// sees the same time the editor scheduled (e.g. "Aug 15, 2026, 2:30 PM EDT"),
// not their own browser timezone.
export function formatScheduledTime(iso) {
  if (!iso) return '';

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) return '';

  // Explicit fields (not dateStyle/timeStyle) so `timeZoneName` is allowed —
  // renders e.g. "Aug 15, 2026, 2:30 PM EDT".
  return d.toLocaleString('en-US', {
    timeZone: EASTERN_TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

// The conflict key for a scheduled push: the exact same campaign (segment) set
// AND the same delivery instant. Two pushes with this key collide even if their
// content differs — we warn the editor before creating another (WWW-15236 #6).
// Campaigns are sorted so selection order doesn't matter, and the time is
// normalized to epoch ms so ISO format differences (with/without millis) match.
export function scheduledConflictKey(message = {}) {
  const t = message.scheduledSendTime ? new Date(message.scheduledSendTime).getTime() : NaN;

  return JSON.stringify({
    campaigns: [...(message.campaignIds || [])].sort(),
    time: Number.isNaN(t) ? null : t,
  });
}

// The existing scheduled pushes that collide with `candidate` (same segments +
// time), skipping the record being edited (same id). Empty when there's no clash.
export function findScheduledConflicts(candidate, existing = [], excludeId) {
  const key = scheduledConflictKey(candidate);

  // A candidate with no campaigns or no time can't meaningfully conflict.
  if (!(candidate.campaignIds || []).length || !candidate.scheduledSendTime) return [];

  return existing.filter((m) => m && m.id !== excludeId && scheduledConflictKey(m) === key);
}

// Zip the campaign ids with the settled Braze schedule results into the
// `brazeScheduleIds` array persisted on the message. A failed schedule keeps its
// campaign id with a null scheduleId so the row still renders and the failure is
// visible rather than silently dropped.
export function buildScheduleIds(campaignIds = [], results = []) {
  return campaignIds.map((campaignId, i) => {
    const outcome = results[i];
    const ok = Boolean(outcome) && outcome.status === 'fulfilled';

    return { campaignId, scheduleId: ok ? (outcome.value?.schedule_id || null) : null };
  });
}
